import { useEffect, useState } from "react"
import { object, func, string } from 'prop-types'
import { ServerRequest } from "../../../utils/apiweb"
import { REQUEST_METHOD } from '@laramie/ui';
import { APIS } from "../../../config/apis"
import { onRequestError, onRequestNoSuccess } from "../../../utils/requests"
import {TIPS} from "../../../consts/tips";

import { CheckboxButton, CheckboxButtonGroup, FormError, InputNumber, Modal, FormLabel, Select } from "../../common"
import { useForm, useFormErrors, useManagedContext } from "../../hooks"
import { isNull, isValidNumber } from "../../../utils/validator"
import { VALIDATION_MESSAGE } from "../../../consts/validationMessage"

const entityInit = {
    id: -Date.now(),
    codigo: '',
    nombre: '',
    orden: '',
    actEmitido: false,
    automatico: false,
    autonumerado: false,
    numero: 0,
    imputacion: 0,
    tipo: '',
}

const TipoMovimientoModal = (props) => {
    const { setIsLoading } = useManagedContext()

    const [entity, setEntity] = useState(entityInit)
    const [formValues, formHandle, _formReset, formSet] = useForm({
        codigo: '',
        nombre: '',
        orden: '',
        actEmitido: false,
        automatico: false,
        autonumerado: false,
        numero: 0,
        imputacion: 0,
        tipo: '',
    })

    useEffect(() => {
        const {
            codigo,
            nombre,
            orden,
            actEmitido,
            automatico,
            autonumerado,
            numero,
            imputacion,
            tipo,
        } = props.editItem

        setEntity({ ...entityInit, ...props.editItem })
        formSet({
            codigo,
            nombre,
            orden,
            actEmitido,
            automatico,
            autonumerado,
            numero,
            imputacion,
            tipo,
        })
    }, [props.editItem])

    const { errorOf, validate } = useFormErrors(getErrors, getFormatErrors)

    function getErrors() {
        const errors = {}

        if (formValues.codigo.length < 1) errors.codigo = VALIDATION_MESSAGE.REQUIRED_SHORT
        if (formValues.nombre.length < 1) errors.nombre = VALIDATION_MESSAGE.REQUIRED
        if (isEmptyValue(formValues.orden)) errors.orden = VALIDATION_MESSAGE.REQUIRED_SHORT
        if (formValues.imputacion == '') errors.imputacion = VALIDATION_MESSAGE.REQUIRED_SHORT
        if (formValues.tipo.length < 1) errors.tipo = VALIDATION_MESSAGE.REQUIRED_SHORT

        return errors
    }

    function getFormatErrors() {
        const errors = {}
        if (props.editMode === 'view') return errors

        if (!isEmptyValue(formValues.orden) && !isValidNumber(formValues.orden, true)) errors.orden = 'Ingresá un número mayor a cero'

        return errors
    }

    function isEmptyValue(valor) {
        return isNull(valor) || valor.toString().length === 0
    }

    const onClickConfirm = () => {
        if (!validate()) return

        setIsLoading(true)

        ServerRequest(
            props.editMode === 'add' ? REQUEST_METHOD.POST : REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.TIPO_MOVIMIENTO,
            props.editMode === 'modify' ? `/${entity.id}` : '',
            { ...entity, ...formValues, numero: parseInt(formValues.numero) ?? 0, orden: parseInt(formValues.orden) ?? 0 },
            res => res.json().then(() => {
                setIsLoading(false)
                props.onConfirm()
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            error => {
                onRequestError(error)
                setIsLoading(false)
            },
        )
    }

    const disabled = props.editMode === 'view'

    return (
        <Modal
            onEscape={props.onCancel}
            title={`Tipo de movimiento: ${props.editItem === 'add' ? 'Nuevo' : entity.nombre}`}
            tipName={TIPS.NAME.MODALS.ENTIDAD_MODAL}
            body={(
                <div className="row form-basic">
                    <div className="col-12 col-md-2">
                        <FormLabel htmlFor="codigo" required>Código</FormLabel>
                        <input
                            type="text"
                            name="codigo"
                            maxLength={50}
                            className={`form-control ${errorOf('codigo') ? 'is-invalid' : ''}`}
                            value={formValues.codigo}
                            onChange={formHandle}
                            disabled={disabled}
                        />
                        <FormError message={errorOf('codigo')} />
                    </div>
                    <div className="col-12 col-md-6">
                        <FormLabel htmlFor="nombre" required>Nombre</FormLabel>
                        <input
                            type="text"
                            name="nombre"
                            className={`form-control ${errorOf('nombre') ? 'is-invalid' : ''}`}
                            value={formValues.nombre}
                            onChange={formHandle}
                            disabled={disabled}
                        />
                        <FormError message={errorOf('nombre')} />
                    </div>
                    <div className="col-12 col-md-2">
                        <FormLabel htmlFor="orden" required>Orden</FormLabel>
                        <input
                            name="orden"
                            type="number"
                            placeholder=""
                            className={`form-control ${errorOf('orden') ? 'is-invalid' : ''}`}
                            value={formValues.orden}
                            onChange={formHandle}
                            disabled={disabled}
                        />
                        <FormError message={errorOf('orden')} />
                    </div>
                    <div className="mb-3 col-12 col-md-2">
                        <label htmlFor="numero" className="form-label">Número</label>
                        <InputNumber
                            name="numero"
                            placeholder=""
                            className="form-control"
                            value={formValues.numero}
                            onChange={formHandle}
                            precision={0}
                            disabled={disabled}
                        />
                        <FormError />
                    </div>
                    <div className="col-12 col-md-2">
                        <FormLabel htmlFor="tipo" required>Tipo</FormLabel>
                        <input
                            name="tipo"
                            type="text"
                            placeholder=""
                            className={`form-control ${errorOf('tipo') ? 'is-invalid' : ''}`}
                            value={formValues.tipo}
                            onChange={formHandle}
                            disabled={disabled}
                        />
                        <FormError message={errorOf('tipo')} />
                    </div>
                    <div className="mb-3 col-12 col-md-2">
                        <FormLabel htmlFor="imputacion" required>Imputación</FormLabel>
                        <Select
                            name="imputacion"
                            className={`form-control ${errorOf('imputacion') ? 'is-invalid' : ''}`}
                            value={formValues.imputacion}
                            onChange={formHandle}
                            options={[{value: 'D', label: 'D'}, {value: 'H', label: 'H'}, {value: 'F', label: 'F'}]}
                            disabled={disabled}
                        />
                        <FormError message={errorOf('imputacion')} />
                    </div>
                    <div className="mb-3 col-12 col-md-8">
                        <CheckboxButtonGroup label="Comportamiento">
                            <CheckboxButton
                                name="actEmitido"
                                label="Act. emitido"
                                checked={formValues.actEmitido}
                                onChange={formHandle}
                                disabled={disabled}
                            />
                            <CheckboxButton
                                name="automatico"
                                label="Automático"
                                checked={formValues.automatico}
                                onChange={formHandle}
                                disabled={disabled}
                            />
                            <CheckboxButton
                                name="autonumerado"
                                label="Autonumerado"
                                checked={formValues.autonumerado}
                                onChange={formHandle}
                                disabled={disabled}
                            />
                        </CheckboxButtonGroup>
                    </div>
                </div>
            )}
            footer={(
                <div className="width-100p disp-b">
                    <button className="btn back-button m-left-5" data-dismiss="modal" onClick={props.onCancel}>Volver</button>
                    {props.editMode !== 'view' && (
                        <button className="btn action-button" data-dismiss="modal" onClick={onClickConfirm}>Guardar</button>
                    )}
                </div>
            )}
        />
    )
}

TipoMovimientoModal.propTypes = {
    editMode: string,
    editItem: object,
    onCancel: func,
    onConfirm: func,
}

export default TipoMovimientoModal
